'use client';

import React, { useState, useRef, useEffect } from 'react';
import type { CabinClass } from './FlightSearchHero';

interface CabinClassSelectorProps {
  value: CabinClass;
  onChange: (v: CabinClass) => void;
}

const CABIN_OPTIONS: { value: CabinClass; label: string; icon: string }[] = [
  { value: 'economy', label: 'Economy', icon: '💺' },
  { value: 'premium_economy', label: 'Premium Economy', icon: '🪑' },
  { value: 'business', label: 'Business', icon: '✈️' },
  { value: 'first', label: 'First Class', icon: '👑' },
];

export default function CabinClassSelector({ value, onChange }: CabinClassSelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = CABIN_OPTIONS.find((o) => o.value === value)!;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-4 py-2.5 border border-border rounded-xl bg-input hover:border-primary text-sm font-medium text-foreground transition-all duration-150"
      >
        <span>{selected.icon}</span>
        <span>{selected.label}</span>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute top-full mt-1.5 left-0 bg-card border border-border rounded-xl card-shadow z-50 w-52 overflow-hidden fade-in">
          {CABIN_OPTIONS.map((opt) => (
            <button
              key={`cabin-${opt.value}`}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-colors duration-100 ${
                opt.value === value
                  ? 'bg-secondary text-primary font-semibold' :'hover:bg-muted text-foreground font-medium'
              }`}
            >
              <span>{opt.icon}</span>
              <span>{opt.label}</span>
              {opt.value === value && (
                <svg className="w-4 h-4 ml-auto text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}